import React, { useEffect, useMemo } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Bell } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import IPhoneMockup from "@/components/IPhoneMockup";
import PopularSearchesGrid from "@/components/PopularSearchesGrid";
import { useLanguage } from "@/context/LanguageContext";
import { useNotify } from "@/context/NotifyContext";
import { useSeoHead } from "@/lib/useSeoHead";
import { SEO_STYLE_MAP, SEO_STYLE_SLUGS } from "@/lib/seoStyles";
import { SEO_CITY_MAP, SEO_CITY_SLUGS, SEO_DISTRICT_MAP, SEO_DISTRICTS_BY_CITY } from "@/lib/seoCities";
import { APP_SCREENS, APP_SCREENS_ALT, LOGOS } from "@/lib/assets";
import { trackScan } from "@/lib/api";
import { track } from "@/lib/analytics";

const ORIGIN = "https://feaststar.app";

export default function CityStyleLanding() {
  const { style, city, district } = useParams();
  const { lang, t } = useLanguage();
  const { openNotify } = useNotify();

  const styleData = SEO_STYLE_MAP[style];
  const cityData = SEO_CITY_MAP[city];
  const districtData = district ? (SEO_DISTRICT_MAP[city] || {})[district] : null;
  const valid = !!styleData && !!cityData && (!district || !!districtData);

  // Compute SEO data (always — hooks must not be conditional)
  const seo = useMemo(() => {
    if (!valid) return null;
    const cityName = lang === "de" ? cityData.nameDe : cityData.nameEn;
    const cityNameDe = cityData.nameDe;
    const cityNameEn = cityData.nameEn;
    const styleLabelDe = styleData.labelDe;
    const styleShortDe = styleData.shortDe;
    const styleShortEn = styleData.shortEn;
    // District-specific augmentation
    const distNameDe = districtData ? districtData.nameDe : null;
    const distNameEn = districtData ? districtData.nameEn : null;
    const fullLocDe = distNameDe ? `${cityNameDe}-${distNameDe}` : cityNameDe;
    const fullLocEn = distNameEn ? `${cityNameEn}-${distNameEn}` : cityNameEn;

    const titleDe = districtData
      ? `${styleShortDe} in ${cityNameDe}-${distNameDe} — Qualität & echte Standards | Feaststar`
      : `${styleShortDe} in ${cityNameDe} — Bio, Qualität & echte Standards | Feaststar`;
    const titleEn = districtData
      ? `${styleShortEn} in ${cityNameEn} ${distNameEn} — Quality & real standards | Feaststar`
      : `${styleShortEn} in ${cityNameEn} — Quality, real standards | Feaststar`;
    const descDe = districtData
      ? `${styleShortDe} in ${cityNameDe}-${distNameDe} finden — gefiltert nach Bio-Standard, Zutatenherkunft, Frische und Bewertungen aus der Community. Feaststar zeigt dir in ${distNameDe} die ${styleLabelDe.toLowerCase()}-Restaurants, die wirklich zu deinen Standards passen.`
      : `${styleShortDe} in ${cityNameDe} finden — gefiltert nach echten Qualitätskriterien wie Zutatenherkunft, Frische, Allergen-Transparenz und Bewertungen aus der Community. Mit Feaststar findest du in ${cityNameDe} ${styleLabelDe.toLowerCase()}-Restaurants, die wirklich zu deinen Standards passen.`;
    const descEn = districtData
      ? `Find ${styleShortEn.toLowerCase()} in ${cityNameEn} ${distNameEn} — filtered by organic standards, ingredient origin, freshness and community ratings. Feaststar shows you the ${styleLabelDe.toLowerCase()} places in ${distNameEn} that truly match your standards.`
      : `Find ${styleShortEn.toLowerCase()} in ${cityNameEn} — filtered by real quality criteria like ingredient origin, freshness, allergen transparency and community ratings. Feaststar shows places in ${cityNameEn} that truly match your standards.`;

    const canonical = districtData
      ? `${ORIGIN}/${styleData.slug}/${cityData.slug}/${districtData.slug}`
      : `${ORIGIN}/${styleData.slug}/${cityData.slug}`;
    const alternates = [
      { hreflang: "de", href: canonical },
      { hreflang: "de-DE", href: canonical },
      { hreflang: "en", href: `${canonical}?lang=en` },
      { hreflang: "x-default", href: canonical },
    ];

    const keywords = lang === "de"
      ? `${styleShortDe} ${fullLocDe}, ${styleData.keywordsDe}, ${fullLocDe} Restaurants, ${styleData.labelDe} essen ${fullLocDe}, Restaurant in der Nähe ${fullLocDe}${distNameDe ? `, ${distNameDe} ${styleShortDe}` : ""}`
      : `${styleShortEn} ${fullLocEn}, ${styleData.keywordsEn}, ${fullLocEn} restaurants, ${styleData.labelEn} dining ${fullLocEn}${distNameEn ? `, ${distNameEn} ${styleShortEn}` : ""}`;

    // JSON-LD blocks — replace {city} with full location label so FAQs feel district-specific
    const replaceLocDe = (txt) => txt.replace(/\{city\}/g, fullLocDe);
    const replaceLocEn = (txt) => txt.replace(/\{city\}/g, fullLocEn);
    const faqsDe = styleData.faqsDe.map((f) => ({
      "@type": "Question",
      name: replaceLocDe(f.q),
      acceptedAnswer: { "@type": "Answer", text: replaceLocDe(f.a) },
    }));
    const faqsEn = styleData.faqsEn.map((f) => ({
      "@type": "Question",
      name: replaceLocEn(f.q),
      acceptedAnswer: { "@type": "Answer", text: replaceLocEn(f.a) },
    }));

    // Breadcrumb dynamic — adds district level if applicable
    const breadcrumbItems = [
      { "@type": "ListItem", position: 1, name: lang === "de" ? "Startseite" : "Home", item: ORIGIN + "/" },
      {
        "@type": "ListItem",
        position: 2,
        name: lang === "de" ? styleLabelDe : styleData.labelEn,
        item: `${ORIGIN}/${styleData.slug}/${cityData.slug}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: cityName,
        item: `${ORIGIN}/${styleData.slug}/${cityData.slug}`,
      },
    ];
    if (districtData) {
      breadcrumbItems.push({
        "@type": "ListItem",
        position: 4,
        name: lang === "de" ? distNameDe : distNameEn,
        item: canonical,
      });
    }

    const jsonLd = [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbItems,
      },
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: lang === "de" ? titleDe : titleEn,
        description: lang === "de" ? descDe : descEn,
        url: canonical,
        inLanguage: lang === "de" ? "de" : "en",
        about: { "@type": "Thing", name: `${styleLabelDe} Restaurant` },
        isPartOf: { "@type": "WebSite", name: "Feaststar", url: ORIGIN + "/" },
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: `${styleLabelDe} Restaurant-Empfehlung`,
        provider: { "@type": "Organization", name: "Feaststar", url: ORIGIN + "/" },
        areaServed: districtData
          ? {
              "@type": "Place",
              name: `${cityNameDe}-${distNameDe}`,
              containedInPlace: {
                "@type": "City",
                name: cityNameDe,
                addressCountry: cityData.country,
              },
            }
          : {
              "@type": "City",
              name: cityNameDe,
              addressCountry: cityData.country,
            },
        description: lang === "de" ? descDe : descEn,
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        inLanguage: lang === "de" ? "de" : "en",
        mainEntity: lang === "de" ? faqsDe : faqsEn,
      },
    ];

    return {
      title: lang === "de" ? titleDe : titleEn,
      description: lang === "de" ? descDe : descEn,
      keywords,
      canonical,
      alternates,
      jsonLd,
      locale: lang === "de" ? "de_DE" : "en_US",
    };
  }, [valid, lang, styleData, cityData, districtData]);

  useSeoHead(seo || {});

  useEffect(() => {
    if (!valid) return;
    const pageKey = district ? `city-style-${style}-${city}-${district}` : `city-style-${style}-${city}`;
    trackScan({ page: pageKey, language: lang });
    track("city_style_view", { style, city, district: district || null, lang });
  }, [style, city, district, valid, lang]);

  if (!valid) {
    return <Navigate to="/" replace />;
  }

  const cityName = lang === "de" ? cityData.nameDe : cityData.nameEn;
  const cityNameDe = cityData.nameDe;
  const styleLabel = lang === "de" ? styleData.labelDe : styleData.labelEn;
  // Full location label: district included when applicable
  const distName = districtData ? (lang === "de" ? districtData.nameDe : districtData.nameEn) : null;
  const fullLoc = distName ? `${cityName}-${distName}` : cityName;
  const heroA = (lang === "de" ? styleData.heroDe.a : styleData.heroEn.a);
  const heroB = (lang === "de" ? styleData.heroDe.b : styleData.heroEn.b).replace("{city}", fullLoc);
  const body = (lang === "de" ? styleData.bodyDe : styleData.bodyEn).replace(/\{city\}/g, fullLoc);
  const whatIs = (lang === "de" ? styleData.whatIsDe : styleData.whatIsEn).replace(/\{city\}/g, fullLoc);
  // When on district page use the district-specific hook; else city hook
  const localHook = districtData
    ? (lang === "de" ? districtData.hookDe : districtData.hookEn)
    : (lang === "de" ? cityData.localHookDe : cityData.localHookEn);
  const faqs = (lang === "de" ? styleData.faqsDe : styleData.faqsEn).map((f) => ({
    q: f.q.replace(/\{city\}/g, fullLoc),
    a: f.a.replace(/\{city\}/g, fullLoc),
  }));
  const cityDistricts = SEO_DISTRICTS_BY_CITY[city] || [];

  // Wähle das passendste iPhone-Screen je nach Stil
  const heroScreen = "search"; // discover list passt am besten zu „Restaurants in X finden"

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
  });
  const fadeUpView = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <div data-testid="city-style-landing" className="relative">
      <Nav />

      {/* HERO */}
      <section className="relative pt-24 lg:pt-28 pb-12 lg:pb-16" data-testid="city-style-hero">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-12 grid lg:grid-cols-12 gap-6 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-4 text-[12px] tracking-widest uppercase text-muted" data-testid="breadcrumb">
              <Link to="/" className="hover:text-primary">Feaststar</Link>
              <span className="mx-2">·</span>
              <span>{styleLabel}</span>
              <span className="mx-2">·</span>
              {districtData ? (
                <>
                  <Link to={`/${style}/${city}`} className="hover:text-primary">{cityName}</Link>
                  <span className="mx-2">·</span>
                  <span className="text-primary">{distName}</span>
                </>
              ) : (
                <span className="text-primary">{cityName}</span>
              )}
            </nav>

            <motion.div {...fade(0)} className="section-label inline-flex items-center gap-2.5" data-testid="city-style-eyebrow">
              <img src={LOGOS.star} alt="" className="w-6 h-6 select-none feaststar-logo" aria-hidden="true" draggable="false" />
              <span>{lang === "de" ? styleData.shortDe.toUpperCase() : styleData.shortEn.toUpperCase()} · {fullLoc.toUpperCase()}</span>
            </motion.div>

            <h1 className="display-xl mt-2" data-testid="city-style-h1">
              <motion.span className="block" {...fade(0.08)}>{heroA}</motion.span>
              <motion.span className="block italic text-accent" {...fade(0.16)}>{heroB}</motion.span>
            </h1>

            <motion.p className="body-lg mt-6 max-w-xl" {...fade(0.24)} data-testid="city-style-sub">
              {localHook}
            </motion.p>

            <motion.div className="flex flex-wrap gap-3 mt-8" {...fade(0.32)}>
              <button
                type="button"
                onClick={() => openNotify("city-style-hero")}
                data-testid="city-style-notify-cta"
                className="btn-primary inline-flex items-center gap-2"
              >
                <Bell className="w-4 h-4" />
                {lang === "de" ? "Beim Launch benachrichtigen" : "Notify me at launch"}
              </button>
              <Link
                to="/"
                data-testid="city-style-learn-more"
                className="btn-ghost inline-flex items-center gap-2"
              >
                {lang === "de" ? "Wie Feaststar funktioniert" : "How Feaststar works"}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-5 flex justify-center lg:justify-end mt-8 lg:mt-0">
            <motion.div {...fade(0.18)}>
              <IPhoneMockup
                width={260}
                image={APP_SCREENS[heroScreen]}
                alt={`Feaststar — ${styleLabel} ${fullLoc}: ${APP_SCREENS_ALT[heroScreen]}`}
                priority
                testId="city-style-iphone"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* AVAILABILITY NOTICE */}
      <section className="px-5 lg:px-12">
        <div className="max-w-[900px] mx-auto border border-soft rounded-sm p-5 lg:p-6 bg-bg flex items-start gap-4" data-testid="availability-notice">
          <span className="relative mt-1 inline-flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping" style={{ background: "var(--color-accent)" }} />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: "var(--color-accent)" }} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] tracking-widest uppercase text-muted mb-1">
              {t("home.origin.availabilityEyebrow")}
            </p>
            <p className="body-md text-primary">
              {lang === "de"
                ? `Die Feaststar-App ist bald verfügbar. Sobald sie live ist, kannst du in ${fullLoc} ${styleLabel.toLowerCase()}-Restaurants direkt filtern. Lass dich beim Launch benachrichtigen — kostenlos, ohne Spam.`
                : `The Feaststar app is launching soon. As soon as it's live, you can filter ${styleLabel.toLowerCase()} restaurants in ${fullLoc} directly. Get notified at launch — free, no spam.`}
            </p>
          </div>
        </div>
      </section>

      {/* WHAT IS X IN Y */}
      <section className="section-pad" data-testid="what-is-section">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <motion.div {...fadeUpView(0)} className="section-label">
              {lang === "de" ? "WAS BEDEUTET DAS?" : "WHAT DOES IT MEAN?"}
            </motion.div>
            <motion.h2 className="display-lg mt-3" {...fadeUpView(0.06)}>
              {lang === "de"
                ? `${styleData.shortDe} in ${fullLoc} — kurz erklärt.`
                : `${styleData.shortEn} in ${fullLoc} — explained.`}
            </motion.h2>
            <motion.p className="body-lg mt-6 whitespace-pre-line" {...fadeUpView(0.12)}>
              {whatIs}
            </motion.p>
          </div>
          <div className="lg:col-span-5">
            <motion.p className="body-lg whitespace-pre-line" {...fadeUpView(0.18)}>
              {body}
            </motion.p>
          </div>
        </div>
      </section>

      {/* CRITERIA TAGS */}
      <section className="section-pad-sm border-t border-soft" data-testid="criteria-tags-section">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-12">
          <motion.div {...fadeUpView(0)} className="section-label">
            {lang === "de" ? "FILTER, DIE FEASTSTAR ANBIETET" : "FILTERS FEASTSTAR OFFERS"}
          </motion.div>
          <motion.h2 className="display-md mt-3 mb-6" {...fadeUpView(0.06)}>
            {lang === "de"
              ? `So findet Feaststar passende ${styleData.shortDe.toLowerCase()} in ${fullLoc}.`
              : `How Feaststar finds matching ${styleData.shortEn.toLowerCase()} in ${fullLoc}.`}
          </motion.h2>
          <p className="body-md max-w-3xl mb-8">
            {lang === "de"
              ? `Feaststar bewertet jeden Betrieb in ${fullLoc} nach 36 gewichteten Qualitäts-Kriterien. Diese Filter sind für ${styleData.shortDe} besonders relevant:`
              : `Feaststar rates every place in ${fullLoc} by 36 weighted quality criteria. These filters are especially relevant for ${styleData.shortEn.toLowerCase()}:`}
          </p>
          <div className="flex flex-wrap gap-2.5" data-testid="criteria-tags">
            {styleData.tags.map((tag, i) => (
              <span
                key={tag}
                data-testid={`criteria-tag-${i}`}
                className="tag-chip active"
              >
                <Check className="w-3.5 h-3.5 mr-1.5 inline-block" />
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* DISTRICT GRID — only on city-level pages with districts */}
      {!districtData && cityDistricts.length > 0 && (
        <section className="section-pad-sm border-t border-soft" data-testid="districts-section">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-12">
            <motion.div {...fadeUpView(0)} className="section-label">
              {lang === "de" ? `STADTVIERTEL IN ${cityName.toUpperCase()}` : `DISTRICTS IN ${cityName.toUpperCase()}`}
            </motion.div>
            <motion.h2 className="display-md mt-3 mb-6" {...fadeUpView(0.06)}>
              {lang === "de"
                ? `${styleData.shortDe} nach Viertel in ${cityNameDe}.`
                : `${styleData.shortEn} by district in ${cityData.nameEn}.`}
            </motion.h2>
            <p className="body-md max-w-3xl mb-8">
              {lang === "de"
                ? `Suche ${styleData.shortDe.toLowerCase()} gezielt nach Stadtviertel in ${cityNameDe} — von ${cityDistricts[0].nameDe} bis ${cityDistricts[cityDistricts.length - 1].nameDe}.`
                : `Search ${styleData.shortEn.toLowerCase()} by district in ${cityData.nameEn} — from ${cityDistricts[0].nameEn} to ${cityDistricts[cityDistricts.length - 1].nameEn}.`}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="districts-grid">
              {cityDistricts.map((d, i) => (
                <Link
                  key={d.slug}
                  to={`/${style}/${city}/${d.slug}`}
                  data-testid={`district-link-${i}`}
                  className="block px-4 py-4 border border-soft rounded-sm hover:border-primary transition-colors group"
                >
                  <span className="block text-[14px] text-primary group-hover:text-accent transition-colors leading-snug">
                    {styleData.shortDe} {lang === "de" ? d.nameDe : d.nameEn}
                  </span>
                  <span className="block text-[11px] tracking-widest uppercase text-muted mt-1">
                    {cityName}-{lang === "de" ? d.nameDe : d.nameEn}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="section-pad" data-testid="faq-section">
        <div className="max-w-[1000px] mx-auto px-5 lg:px-12">
          <motion.div {...fadeUpView(0)} className="section-label">
            {lang === "de" ? "FRAGEN" : "QUESTIONS"}
          </motion.div>
          <motion.h2 className="display-lg mt-3 mb-8" {...fadeUpView(0.06)}>
            {lang === "de"
              ? `${styleData.shortDe} in ${fullLoc} — FAQ.`
              : `${styleData.shortEn} in ${fullLoc} — FAQ.`}
          </motion.h2>
          <div className="space-y-6">
            {faqs.map((item, i) => (
              <motion.div
                key={i}
                {...fadeUpView(0.06 + i * 0.04)}
                className="pb-6 border-b border-soft last:border-b-0"
                data-testid={`faq-${i}`}
              >
                <h3 className="display-sm mb-2">{item.q}</h3>
                <p className="body-md">{item.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad-sm" data-testid="cta-section">
        <div className="max-w-[1000px] mx-auto px-5 lg:px-12 text-center">
          <h2 className="display-md mb-4">
            {lang === "de"
              ? `Werde benachrichtigt, sobald Feaststar in ${fullLoc} live geht.`
              : `Get notified when Feaststar launches in ${fullLoc}.`}
          </h2>
          <p className="body-md max-w-2xl mx-auto mb-6">
            {lang === "de"
              ? "Eine Nachricht. Kein Newsletter-Müll. Du erfährst es als Erstes, sobald die App verfügbar ist."
              : "One message. No newsletter clutter. You're among the first to know once the app is available."}
          </p>
          <button
            type="button"
            onClick={() => openNotify(`city-style-cta-${style}-${city}${district ? "-" + district : ""}`)}
            data-testid="city-style-cta-notify"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Bell className="w-4 h-4" />
            {lang === "de" ? "Beim Launch benachrichtigen" : "Notify me at launch"}
          </button>
        </div>
      </section>

      {/* POPULAR: same style+city, sibling districts (only on district pages) */}
      {districtData && cityDistricts.length > 1 && (
        <PopularSearchesGrid
          mode="sibling-districts"
          currentStyle={style}
          currentCity={city}
          currentDistrict={district}
          lang={lang}
          headingDe={`${styleData.shortDe} in anderen Vierteln von ${cityNameDe}`}
          headingEn={`${styleData.shortEn} in other ${cityData.nameEn} districts`}
          subDe={`Bleib bei ${styleData.shortDe.toLowerCase()} — finde sie in einem anderen Viertel von ${cityNameDe}.`}
          subEn={`Stay with ${styleData.shortEn.toLowerCase()} — find them in another district of ${cityData.nameEn}.`}
          limit={12}
        />
      )}

      {/* POPULAR: same style, other cities */}
      <PopularSearchesGrid
        mode="style"
        currentStyle={style}
        currentCity={city}
        lang={lang}
        headingDe={`${styleData.shortDe} in anderen Städten`}
        headingEn={`${styleData.shortEn} in other cities`}
        subDe={`Feaststar zeigt ${styleData.shortDe.toLowerCase()} in der gesamten DACH-Region.`}
        subEn={`Feaststar shows ${styleData.shortEn.toLowerCase()} across the DACH region.`}
        limit={20}
      />

      {/* POPULAR: same city, other styles */}
      <PopularSearchesGrid
        mode="city"
        currentStyle={style}
        currentCity={city}
        lang={lang}
        headingDe={`Andere Ernährungsstile in ${cityNameDe}`}
        headingEn={`Other diet styles in ${cityData.nameEn}`}
        subDe={`Restaurants in ${cityNameDe} — gefiltert nach deinem Ernährungsstil.`}
        subEn={`Restaurants in ${cityData.nameEn} — filtered by your diet style.`}
        limit={10}
      />

      <Footer variant="home" />
    </div>
  );
}
